import {
  FORM_FIELD_WIDTH,
  FORM_FIELD_WIDTH_UNITS,
} from 'ui/screens/EditPatient/PatientDetails/consts'
import { getNestedObjectValue } from 'utilities/objects'

/**
 *
 * @param {object} patient - the patient object
 * @returns {object} provider - an object with the provider's information
 */
export const constructProviderFromPatient = (patient) => ({
  first_name:
    patient?.identifiableInfo?.demographicData?.providerInfo?.firstName,
  last_name: patient?.identifiableInfo?.demographicData?.providerInfo?.lastName,
  address: {
    address_line_1:
      patient?.identifiableInfo?.demographicData?.providerAddress?.addressLine1,
    address_line_2:
      patient?.identifiableInfo?.demographicData?.providerAddress?.addressLine2,
    city: patient?.identifiableInfo?.demographicData?.providerAddress?.city,
    state: patient?.identifiableInfo?.demographicData?.providerAddress?.state,
    zip_code:
      patient?.identifiableInfo?.demographicData?.providerAddress?.zipCode,
  },
  npi: patient?.identifiableInfo?.demographicData?.providerNpi,
})

/**
 * Get the width of a field based on the layout and unit
 * @param {object} layout - the layout of the field, e.g. half, full
 * @param {string} unit - the unit of the width, e.g. percentage, numeric
 * @returns {string} - the width of the field in the specified unit
 */
export const getFieldWidth = ({ layout, unit }) => {
  const layoutConfig = FORM_FIELD_WIDTH[layout]

  return unit === FORM_FIELD_WIDTH_UNITS.PERCENTAGE
    ? `${layoutConfig.value * 100}%`
    : layoutConfig.value
}

export const transformPatientContactInfo = (contactInfo) => {
  const transformedContactInfo = { ...contactInfo }

  const removeWhiteSpaces = (phone) => phone.replace(/\s/g, '')

  const fieldsToTransform = {
    alternatePhoneNumber: (phone) => removeWhiteSpaces(phone) || null,
    phoneNumber: (phone) => removeWhiteSpaces(phone) || null,
  }

  Object.entries(fieldsToTransform).forEach(([field, transformFunction]) => {
    const fieldValue = getNestedObjectValue({
      object: contactInfo,
      path: field,
    })

    if (fieldValue || fieldValue === '' || fieldValue === 0) {
      let transformedValue = transformFunction(contactInfo[field])
      transformedContactInfo[field] = transformedValue
    }
  })
  return transformedContactInfo
}

/**
 * Transform the demographic data to the format expected by the API
 * @param {object} demographicData - the demographic data to transform
 * @returns {object} - the transformed demographic data
 */
export const transformDemographicData = (demographicData) => {
  const transformedDemographicData = { ...demographicData }

  const fieldsToTransform = {
    otherConditions: (conditions) =>
      conditions.map((condition) => condition.id),
    geneticMarkers: (markers) => markers.map((marker) => marker.id),
    height: parseInt,
    providerNpi: (npi) => npi.toString(),
    weight: parseInt,
  }

  Object.entries(fieldsToTransform).forEach(([field, transformFunction]) => {
    const fieldValue = getNestedObjectValue({
      object: demographicData,
      path: field,
    })

    if (fieldValue || fieldValue === '' || fieldValue === 0) {
      let transformedValue = transformFunction(demographicData[field])

      if (
        (field === 'height' || field === 'weight') &&
        (transformedValue.trim === '' || isNaN(transformedValue))
      ) {
        transformedValue = null
      }

      transformedDemographicData[field] = transformedValue
    }
  })

  return transformedDemographicData
}
